import { useState, useEffect } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, MONO } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Label, Row, Ic, dIc } from './shared';
import * as api from '../lib/api';

// Re-export DebtEntryScreen from its dedicated module
export { default as DebtEntryScreen } from './DebtEntryScreen';

export function StrategyScreen({ navigate, debts }) {
  const [strategy, setStrategy] = useState('hybrid'); // 'snowball', 'hybrid', or 'avalanche'
  const [freedomDate, setFreedomDate] = useState('Apr 2027');
  const [isAnimating, setIsAnimating] = useState(false);
  const [localDebts, setLocalDebts] = useState(debts);
  
  const target = localDebts.find(d => d.is_target) || localDebts[0];
  
  // Calculate freedom date based on strategy
  const calculateFreedomDate = (selectedStrategy) => {
    const currentDate = new Date();
    let monthsToAdd;
    
    if (selectedStrategy === 'avalanche') {
      monthsToAdd = 22; // Fastest (interest-focused)
    } else if (selectedStrategy === 'snowball') {
      monthsToAdd = 26; // Slowest (motivation-focused)
    } else {
      monthsToAdd = 24; // Balanced hybrid
    }
    
    currentDate.setMonth(currentDate.getMonth() + monthsToAdd);
    return currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };
  
  const handleStrategyChange = (newStrategy) => {
    if (newStrategy === strategy) return;
    
    setIsAnimating(true);
    setStrategy(newStrategy);
    
    // Animate the date change
    setTimeout(() => {
      setFreedomDate(calculateFreedomDate(newStrategy));
      setTimeout(() => setIsAnimating(false), 600);
    }, 150);
  };
  
  // Cycle to next target debt
  const cycleToNextTarget = async () => {
    if (localDebts.length <= 1) return; // Need at least 2 debts to cycle
    
    const currentIndex = localDebts.findIndex(d => d.is_target);
    const nextIndex = (currentIndex + 1) % localDebts.length;
    const nextDebt = localDebts[nextIndex];
    
    try {
      // Update on backend
      await api.setTargetDebt(nextDebt.id);
      
      // Update local state
      const updatedDebts = localDebts.map((d, i) => ({
        ...d,
        is_target: i === nextIndex
      }));
      setLocalDebts(updatedDebts);
    } catch (err) {
      console.error('Failed to set target debt:', err);
    }
  };
  
  const strategyLabels = {
    snowball: { name: 'Snowball Method', desc: 'Pay smallest debts first for quick wins' },
    hybrid: { name: 'Hybrid Strategy', desc: 'Balanced approach combining both methods' },
    avalanche: { name: 'Avalanche Method', desc: 'Pay highest interest first to save money' }
  };
  
  return (
    <div data-testid="strategy-screen" className="dettio-screen">
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <Label>STRATEGY SELECTOR</Label>
        
        {/* Premium Strategy Toggle */}
        <div style={{ 
          marginBottom: 24,
          padding: 20,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{ 
            display: 'flex', 
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: 12,
            padding: 4,
            position: 'relative',
            marginBottom: 16
          }}>
            {/* Sliding Neon Glow Background - calculates position for 3 options */}
            <div 
              className="strategy-toggle-glow"
              style={{ 
                position: 'absolute',
                top: 4,
                left: strategy === 'snowball' ? '4px' : strategy === 'hybrid' ? 'calc(33.33% + 2px)' : 'calc(66.67% + 1px)',
                width: 'calc(33.33% - 6px)',
                height: 'calc(100% - 8px)',
                background: 'linear-gradient(135deg, #00FFCC 0%, #14B8A6 100%)',
                borderRadius: 8,
                boxShadow: '0 0 20px rgba(0, 255, 204, 0.6), 0 0 40px rgba(0, 255, 204, 0.3)',
                transition: 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 0
              }}
            />
            
            {/* Toggle Buttons - All 3 strategies */}
            {['snowball', 'hybrid', 'avalanche'].map((s) => (
              <button
                key={s}
                onClick={() => handleStrategyChange(s)}
                style={{
                  flex: 1,
                  padding: '12px 8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: MONO,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: strategy === s ? '#000' : 'rgba(255, 255, 255, 0.6)',
                  transition: 'color 0.3s ease',
                  position: 'relative',
                  zIndex: 1,
                  textTransform: 'uppercase'
                }}
              >
                {s}
              </button>
            ))}
          </div>
          
          {/* Strategy Description */}
          <Mo style={{ 
            fontSize: 11, 
            color: 'var(--mute)', 
            textAlign: 'center',
            lineHeight: 1.6,
            display: 'block'
          }}>
            {strategyLabels[strategy].desc}
          </Mo>
        </div>
        
        <Label>ACTIVE STRATEGY</Label>
        <div style={{ 
          padding: 20, 
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.1)', 
          marginBottom: 24, 
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <span className="str-b">{Ic.flame(22, GOLD)}</span>
            <div>
              <Dp style={{ fontSize: 18, fontWeight: 800, color: 'var(--light)' }}>{strategyLabels[strategy].name} Strategy</Dp>
              <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block' }}>{strategyLabels[strategy].desc}</Bd>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { l: 'FREE BY', v: freedomDate, c: GOLD, animate: isAnimating }, 
              { l: 'INTEREST', v: strategy === 'avalanche' ? '$2,180' : strategy === 'hybrid' ? '$2,450' : '$2,820', c: EMBER }, 
              { l: 'EXTRA/MO', v: '$86', c: LIGHT }
            ].map((s, i) => (
              <div 
                key={i} 
                style={{ 
                  flex: 1, 
                  padding: 12,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  textAlign: 'center', 
                  borderRadius: 8,
                  transition: 'all 0.3s ease'
                }}
              >
                <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>{s.l}</Mo>
                <Mo 
                  className={s.animate ? 'freedom-date-tick' : ''}
                  style={{ 
                    fontSize: 14, 
                    fontWeight: 700, 
                    color: s.c,
                    textShadow: s.animate && i === 0 ? '0 0 10px rgba(232, 168, 73, 0.6)' : 'none'
                  }}
                >
                  {s.v}
                </Mo>
              </div>
            ))}
          </div>
        </div>
        {target && <>
          <Label>TARGET DEBT</Label>
          <div className="soft-b" style={{ padding: '16px 0', borderBottom: `1px solid ${FAINT}`, display: 'flex', alignItems: 'center', gap: 12, borderLeft: `3px solid ${GOLD}`, paddingLeft: 16 }}>
            {dIc(target.type)}
            <div style={{ flex: 1 }}>
              <Bd style={{ fontSize: 15, fontWeight: 600, color: 'var(--light)' }}>{target.name}</Bd>
              <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>${target.balance.toLocaleString()} remaining · {target.pct_paid}% conquered</Mo>
            </div>
            <button 
              onClick={cycleToNextTarget}
              data-testid="next-target-btn"
              disabled={localDebts.length <= 1}
              style={{ 
                fontSize: 9, 
                fontWeight: 700, 
                padding: '6px 12px', 
                background: localDebts.length > 1 ? `${GOLD}25` : 'rgba(255, 255, 255, 0.05)', 
                color: localDebts.length > 1 ? 'var(--gold)' : 'var(--mute)',
                letterSpacing: 1.5, 
                borderRadius: 8,
                border: `1px solid ${localDebts.length > 1 ? `${GOLD}40` : 'rgba(255, 255, 255, 0.1)'}`,
                cursor: localDebts.length > 1 ? 'pointer' : 'not-allowed',
                fontFamily: MONO,
                transition: 'all 0.2s ease',
                opacity: localDebts.length > 1 ? 1 : 0.5
              }}
            >
              NEXT
            </button>
          </div>
        </>}
        <Label>TOOLS</Label>
        <Row icon={Ic.plan(18, GOLD)} title="Payoff Timeline" desc="Interactive month-by-month projection" onClick={() => navigate('timeline')} />
        <Row icon={Ic.wand(18, MUTE)} title="What-If Simulator" desc="Test scenarios before committing" onClick={() => navigate('whatif')} />
        <Row icon={Ic.plus(18, GOLD)} title="Manage Debts" desc="Add, edit, or remove debts" onClick={() => navigate('debt-entry')} />
        <Row icon={Ic.chart(18, MUTE)} title="Budget Targets" desc="Set spending limits per category" onClick={() => navigate('budget')} />
      </div>
    </div>
  );
}

export function WhatIfScreen({ debts = [] }) {
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState(null);

  // Select first debt by default if available
  useEffect(() => {
    if (debts.length > 0 && !selectedDebt) {
      setSelectedDebt(debts[0]);
    }
  }, [debts, selectedDebt]);

  const scenarios = [
    { id: 'lump', label: 'Lump Sum Payment', desc: 'What if you made a one-time extra payment?', inputLabel: 'Extra payment amount', inputPrefix: '$' },
    { id: 'extra', label: 'Extra Monthly', desc: 'What if you paid extra each month?', inputLabel: 'Extra monthly amount', inputPrefix: '$' },
    { id: 'refinance', label: 'Refinance', desc: 'What if you got a lower interest rate?', inputLabel: 'New APR', inputSuffix: '%' },
    { id: 'pause', label: 'Pause Payments', desc: 'What happens if you stop paying?', inputLabel: 'Months to pause', inputSuffix: 'mo' },
  ];

  // Helper functions for calculations
  const calculatePayoffMonths = (balance, apr, monthlyPayment) => {
    if (balance <= 0 || monthlyPayment <= 0) return 0;
    const monthlyRate = apr / 12;
    if (monthlyRate === 0) return Math.ceil(balance / monthlyPayment);
    const minInterestPayment = balance * monthlyRate;
    if (monthlyPayment <= minInterestPayment) return 999;
    const months = Math.log(monthlyPayment / (monthlyPayment - balance * monthlyRate)) / Math.log(1 + monthlyRate);
    return Math.ceil(months);
  };

  const calculateTotalInterest = (balance, apr, monthlyPayment) => {
    const months = calculatePayoffMonths(balance, apr, monthlyPayment);
    if (months >= 999) return balance * (apr / 12) * 120;
    return Math.max(0, (months * monthlyPayment) - balance);
  };

  const calculateMonthlyPayment = (balance, apr, months) => {
    if (months <= 0) return 0;
    const monthlyRate = apr / 12;
    if (monthlyRate === 0) return balance / months;
    return (balance * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
  };

  // Calculate results when input changes
  useEffect(() => {
    if (!scenario || !inputValue || !selectedDebt) {
      setResult(null);
      return;
    }

    const value = parseFloat(inputValue) || 0;
    if (value <= 0) {
      setResult(null);
      return;
    }

    const balance = selectedDebt.balance || 0;
    const apr = (selectedDebt.apr || 0) / 100;
    const monthlyRate = apr / 12;
    const minPayment = selectedDebt.min_payment || 50;

    let calculatedResult = {};

    switch (scenario) {
      case 'lump': {
        const newBalance = Math.max(0, balance - value);
        const monthsWithLump = calculatePayoffMonths(newBalance, apr, minPayment);
        const monthsWithoutLump = calculatePayoffMonths(balance, apr, minPayment);
        const monthsSaved = Math.max(0, monthsWithoutLump - monthsWithLump);
        const interestWithout = calculateTotalInterest(balance, apr, minPayment);
        const interestWith = calculateTotalInterest(newBalance, apr, minPayment);
        const interestSaved = Math.max(0, interestWithout - interestWith);
        calculatedResult = {
          primary: { label: 'MONTHS SAVED', value: monthsSaved, color: GOLD },
          secondary: { label: 'INTEREST SAVED', value: `$${Math.round(interestSaved).toLocaleString()}`, color: '#22c55e' },
          description: monthsSaved > 0 
            ? `A $${value.toLocaleString()} payment would shave ${monthsSaved} month${monthsSaved !== 1 ? 's' : ''} off your payoff!`
            : `This payment would help, but your balance is already manageable.`
        };
        break;
      }
      case 'extra': {
        const newMonthlyPayment = minPayment + value;
        const monthsWithExtra = calculatePayoffMonths(balance, apr, newMonthlyPayment);
        const monthsWithoutExtra = calculatePayoffMonths(balance, apr, minPayment);
        const monthsSaved = Math.max(0, monthsWithoutExtra - monthsWithExtra);
        const interestWithout = calculateTotalInterest(balance, apr, minPayment);
        const interestWith = calculateTotalInterest(balance, apr, newMonthlyPayment);
        const interestSaved = Math.max(0, interestWithout - interestWith);
        const payoffDate = new Date();
        payoffDate.setMonth(payoffDate.getMonth() + monthsWithExtra);
        const payoffDateStr = payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        calculatedResult = {
          primary: { label: 'NEW PAYOFF DATE', value: payoffDateStr, color: GOLD },
          secondary: { label: 'INTEREST SAVED', value: `$${Math.round(interestSaved).toLocaleString()}`, color: '#22c55e' },
          description: `Paying $${value} extra per month saves $${Math.round(interestSaved).toLocaleString()} in interest and ${monthsSaved} months!`
        };
        break;
      }
      case 'refinance': {
        const newApr = value / 100;
        const oldMonthlyPayment = calculateMonthlyPayment(balance, apr, 60);
        const newMonthlyPayment = calculateMonthlyPayment(balance, newApr, 60);
        const monthlyDiff = oldMonthlyPayment - newMonthlyPayment;
        const oldTotalInterest = calculateTotalInterest(balance, apr, minPayment);
        const newTotalInterest = calculateTotalInterest(balance, newApr, minPayment);
        const interestSaved = Math.max(0, oldTotalInterest - newTotalInterest);
        calculatedResult = {
          primary: { label: 'MONTHLY SAVINGS', value: `$${Math.round(monthlyDiff).toLocaleString()}`, color: GOLD },
          secondary: { label: 'TOTAL INTEREST SAVED', value: `$${Math.round(interestSaved).toLocaleString()}`, color: '#22c55e' },
          description: monthlyDiff > 0
            ? `Refinancing from ${selectedDebt.apr}% to ${value}% saves $${Math.round(monthlyDiff)}/month!`
            : `A ${value}% rate would actually cost more. Current rate is better.`
        };
        break;
      }
      case 'pause': {
        const monthsPaused = Math.round(value);
        const accruedInterest = balance * monthlyRate * monthsPaused;
        const newBalance = balance + accruedInterest;
        const monthsToPayoff = calculatePayoffMonths(newBalance, apr, minPayment);
        const payoffDate = new Date();
        payoffDate.setMonth(payoffDate.getMonth() + monthsPaused + monthsToPayoff);
        const payoffDateStr = payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        calculatedResult = {
          primary: { label: 'INTEREST ACCRUED', value: `$${Math.round(accruedInterest).toLocaleString()}`, color: EMBER },
          secondary: { label: 'NEW PAYOFF DATE', value: payoffDateStr, color: 'var(--mute)' },
          description: `Pausing ${monthsPaused} month${monthsPaused !== 1 ? 's' : ''} adds $${Math.round(accruedInterest).toLocaleString()} in interest.`
        };
        break;
      }
      default:
        calculatedResult = null;
    }

    setResult(calculatedResult);
  }, [scenario, inputValue, selectedDebt]);

  const activeScenario = scenarios.find(s => s.id === scenario);

  return (
    <div data-testid="whatif-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <Label>WHAT-IF SIMULATOR</Label>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 16, lineHeight: 1.5 }}>Test different strategies before committing. See the exact impact on your debt-free date.</Bd>
        
        {/* Debt Selector */}
        {debts.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>SELECT DEBT</Mo>
            <select
              data-testid="debt-selector"
              value={selectedDebt?.id || ''}
              onChange={(e) => {
                const debt = debts.find(d => d.id === e.target.value);
                setSelectedDebt(debt);
                setResult(null);
              }}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'var(--surface)',
                border: '1px solid var(--faint)',
                borderRadius: 10,
                color: 'var(--light)',
                fontFamily: MONO,
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {debts.map(d => (
                <option key={d.id} value={d.id}>{d.name} - ${d.balance?.toLocaleString()}</option>
              ))}
            </select>
          </div>
        )}

        {debts.length === 0 && (
          <div style={{ padding: 20, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 12, marginBottom: 16, textAlign: 'center' }}>
            <Bd style={{ fontSize: 13, color: 'var(--mute)' }}>Add a debt to start simulating scenarios</Bd>
          </div>
        )}

        {/* Scenario Selection */}
        {selectedDebt && scenarios.map(s => (
          <div key={s.id} onClick={() => { setScenario(s.id); setInputValue(''); setResult(null); }} data-testid={`scenario-${s.id}`} style={{ padding: 16, background: scenario === s.id ? `${GOLD}10` : 'var(--surface)', border: `1px solid ${scenario === s.id ? GOLD : FAINT}`, marginBottom: 10, cursor: 'pointer', borderRadius: 12 }}>
            <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{s.label}</Bd>
            <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 4 }}>{s.desc}</Mo>
          </div>
        ))}

        {/* Input Field */}
        {scenario && activeScenario && selectedDebt && (
          <div className="fade-in" style={{ marginTop: 16, padding: 16, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 12 }}>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>{activeScenario.inputLabel?.toUpperCase()}</Mo>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {activeScenario.inputPrefix && (
                <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--mute)' }}>{activeScenario.inputPrefix}</Mo>
              )}
              <input
                type="number"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="0"
                data-testid={`scenario-input-${scenario}`}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  background: 'var(--faint)',
                  border: '1px solid var(--ghost)',
                  borderRadius: 8,
                  color: 'var(--light)',
                  fontFamily: MONO,
                  fontSize: 20,
                  fontWeight: 700,
                  textAlign: 'center',
                  outline: 'none',
                }}
              />
              {activeScenario.inputSuffix && (
                <Mo style={{ fontSize: 14, fontWeight: 600, color: 'var(--mute)' }}>{activeScenario.inputSuffix}</Mo>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="fade-in" data-testid="scenario-result" style={{ marginTop: 16, padding: 16, background: `${GOLD}06`, border: `1px solid ${GOLD}20`, borderRadius: 12 }}>
            <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>SCENARIO RESULT</Mo>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1, padding: 12, background: 'var(--faint)', textAlign: 'center', borderRadius: 8 }}>
                <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 4 }}>{result.primary.label}</Mo>
                <Dp style={{ fontSize: 22, fontWeight: 800, color: result.primary.color }}>{result.primary.value}</Dp>
              </div>
              <div style={{ flex: 1, padding: 12, background: 'var(--faint)', textAlign: 'center', borderRadius: 8 }}>
                <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 4 }}>{result.secondary.label}</Mo>
                <Dp style={{ fontSize: 22, fontWeight: 800, color: result.secondary.color }}>{result.secondary.value}</Dp>
              </div>
            </div>
            <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.5 }}>{result.description}</Bd>
          </div>
        )}
      </div>
    </div>
  );
}

export function BudgetScreen() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.getBudget().then(r => { setCats(r.data.categories || []); setLoading(false); }).catch(() => setLoading(false)); }, []);

  return (
    <div data-testid="budget-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <Dp style={{ fontSize: 18, fontWeight: 800, color: 'var(--light)' }}>Budget Targets</Dp>
          <Mo style={{ fontSize: 9, fontWeight: 700, padding: '4px 8px', background: `${GOLD}15`, color: 'var(--gold)', letterSpacing: 1.5 }}>PREMIUM</Mo>
        </div>
        {loading ? <Mo style={{ color: 'var(--mute)', textAlign: 'center', display: 'block' }}>Loading...</Mo> : cats.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="1.5" style={{ marginBottom: 16 }}><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 8 }}>No budget targets yet</Dp>
            <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6 }}>Set spending limits for categories like dining, shopping, and subscriptions to stay on track.</Bd>
          </div>
        ) : cats.map((c, i) => { const p = (c.spent / c.target) * 100; return (
          <div key={i} className={p >= 100 ? 'emb-p' : ''} style={{ padding: '14px 0', borderBottom: `1px solid ${FAINT}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{c.name}</Bd>
              <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--light)' }}>${c.spent}<span style={{ color: 'var(--mute)' }}>/${c.target}</span></Mo>
            </div>
            <div style={{ height: 3, background: 'var(--faint)' }}><div style={{ height: '100%', width: `${Math.min(100, p)}%`, background: p < 60 ? GOLD : EMBER }} /></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <Mo style={{ fontSize: 9, color: p < 100 ? GOLD : EMBER }}>${c.target - c.spent} left</Mo>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>{Math.round(p)}%</Mo>
            </div>
          </div>
        ); })}
      </div>
    </div>
  );
}
